import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FilterState } from '../components/FilterDrawer';

/**
 * Comparison mode lets a dashboard show each selected value of a filter
 * dimension (e.g. Region A vs Region B) side by side, instead of the default
 * behaviour which unions the selected users and shows a single blended average.
 *
 * State persists across sessions (localStorage), mirroring usePersistedFilters.
 */

/** Filter fields that can be compared across — every list-valued FilterState key. */
export type ComparisonDimension = 'region' | 'depot' | 'department' | 'jobRole' | 'ageRange' | 'sex' | 'shiftPattern';

export interface ComparisonModeState {
  enabled: boolean;
  dimension: ComparisonDimension | null;
}

/** Max groups we fan out per chart, to keep charts readable and request volume sane. */
export const MAX_COMPARISON_GROUPS = 6;

/** Dimensions in priority order (region first, per product intent). */
const DIMENSION_ORDER: ComparisonDimension[] = [
  'region',
  'depot',
  'department',
  'jobRole',
  'ageRange',
  'sex',
  'shiftPattern',
];

const DIMENSION_LABELS: Record<ComparisonDimension, string> = {
  region: 'Region',
  depot: 'Depot',
  department: 'Department',
  jobRole: 'Job role',
  ageRange: 'Age range',
  sex: 'Sex',
  shiftPattern: 'Shift pattern',
};

export const comparisonDimensionLabel = (d: ComparisonDimension): string => DIMENSION_LABELS[d];

/** A filter dimension is eligible for comparison when 2+ of its values are selected. */
export const eligibleDimensions = (filters: FilterState): ComparisonDimension[] =>
  DIMENSION_ORDER.filter((d) => (filters[d]?.length ?? 0) >= 2);

const STORAGE_KEY = 'peak4.admin.comparison.v1';

const loadState = (key: string): ComparisonModeState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ComparisonModeState> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      enabled: Boolean(parsed.enabled),
      dimension: typeof parsed.dimension === 'string' ? (parsed.dimension as ComparisonDimension) : null,
    };
  } catch {
    return null;
  }
};

export interface UseComparisonModeResult {
  /** Comparison is on AND a valid dimension is selected. */
  enabled: boolean;
  /** The active comparison dimension, or null when not comparing. */
  dimension: ComparisonDimension | null;
  /** Dimensions currently eligible (2+ selected values). */
  eligible: ComparisonDimension[];
  /** Whether the toggle should be interactive at all. */
  canCompare: boolean;
  /** Turn comparison on/off. */
  setEnabled: (enabled: boolean) => void;
  /** Choose which eligible dimension to compare by. */
  setDimension: (dimension: ComparisonDimension) => void;
  label: (d: ComparisonDimension) => string;
}

export const useComparisonMode = (filters: FilterState, options?: { storageKey?: string }): UseComparisonModeResult => {
  const storageKey = options?.storageKey ?? STORAGE_KEY;
  const [state, setState] = useState<ComparisonModeState>(
    () => loadState(storageKey) ?? { enabled: false, dimension: null },
  );

  const eligible = useMemo(() => eligibleDimensions(filters), [filters]);
  const canCompare = eligible.length > 0;

  // Resolve the effective dimension: keep the chosen one if still eligible,
  // otherwise fall back to the first eligible dimension.
  const dimension = useMemo<ComparisonDimension | null>(() => {
    if (!canCompare) return null;
    if (state.dimension && eligible.includes(state.dimension)) {
      return state.dimension;
    }
    return eligible[0];
  }, [canCompare, eligible, state.dimension]);

  // Comparison is only truly active when enabled AND a valid dimension exists.
  const enabled = state.enabled && canCompare && dimension !== null;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }, [state, storageKey]);

  const setEnabled = useCallback((next: boolean) => {
    setState((prev) => ({ ...prev, enabled: next }));
  }, []);

  const setDimension = useCallback((next: ComparisonDimension) => {
    setState((prev) => ({ ...prev, dimension: next, enabled: true }));
  }, []);

  return {
    enabled,
    dimension: enabled ? dimension : null,
    eligible,
    canCompare,
    setEnabled,
    setDimension,
    label: comparisonDimensionLabel,
  };
};
